import { nextTick } from 'vue';
import { httpMgr } from '../../common/network/httpMgr';
import { ApiEnums, CouponPromoTypeEnum } from '../../constants/apiConstants';
import store from '../store';
import { getSystemCode } from '../../common/env/env';
import nativeMgr from '../../common/native/native';

/** 用户奖励金信息 */
export interface UserDiscountData {
  userNewId: number;
  /** 积分抵扣开关 */
  pointSwitch: boolean;
  /** 积分数量 */
  points: number;
  /** 积分可抵扣金额 */
  amount: number;
}

/** 商品优惠信息项 */
export interface PromoInfo {
  guid: string;
  promotionId: string;
  batchCode: string;
  promotionName: string;
  /** 优惠类型 1-营销优惠 3-营销活动 4-新奖励金 */
  couponPromoType: string;
  /** 优惠券面值 */
  worth: string;
  /** 可用积分（两位小数） */
  points: string;
}

/** 商品优惠决策返回项 */
export interface GoodsPromoItem {
  spuId: string;
  skuId: string;
  salePrice: string;
  originalPrice: string;
  /** 优惠后价格（服务端计算） */
  offerPrice?: number;
  promotionInfos: PromoInfo[];
  optionalPromotions: PromoInfo[];
  disabledPromotions: PromoInfo[];
}

/**
 * 卡券用户优惠信息查询
 * @ref https://yapi.hellobike.cn/project/2657/interface/api/433939
 */
export const fetchUserDiscount = async (): Promise<UserDiscountData | null> => {
  const res = await httpMgr.request(ApiEnums.USER_DISCOUNT, {});
  return res.data;
};

/**
 * 卡券用户积分使用开关
 * @ref https://yapi.hellobike.cn/project/2657/interface/api/436173
 */
export const updatePointSwitch = async (pointSwitch: boolean): Promise<boolean> => {
  const res = await httpMgr.request(ApiEnums.USER_POINT_SWITCH, { pointSwitch });
  return res.data;
};

/**
 * 卡券商品优惠决策
 * @ref https://yapi.hellobike.cn/project/6782/interface/api/435391
 */
export const fetchGoodsPromo = async (data: {
  spuIds: string[];
  userNewId?: number;
  /** 当前所在分类 */
  categoryId: string;
  /** 算法出价开关 */
  algorithmPriceFlag?: string;
  /** 应用场景 */
  applicationScene?: string;
  cityCode?: string;
  _uuid?: string;
  systemCode?: string;
}): Promise<GoodsPromoItem[] | null> => {
  const res = await httpMgr.request(ApiEnums.GOODS_PROMO, data);
  return res.data;
};


/**
 * 购买前刷新数据（需求变更V2）
 *
 * 刷新策略：
 * - 列表页入口：promotionInfos 有数据 → 刷新列表；有奖励金优惠 → 额外刷新奖励金
 * - 详情页入口：promotionInfos 有数据 → 刷新列表+详情；勾选了奖励金 → 额外刷新奖励金
 *              promotionInfos 无数据但 optionalPromotions 有数据且勾选了 → 刷新全部
 * - 刷新顺序：如需刷新奖励金，先请求奖励金接口，再请求列表/详情接口
 */
export const refreshRewardData = async (options: {
  spuIds: string[];
  /** 当前所在分类 */
  categoryId: string;
  /** 算法出价开关 */
  algorithmPriceFlag?: string;
  /** 是否来自详情页/浮层入口 */
  isFromDetail?: boolean;
  /** 是否需要刷新奖励金接口 */
  shouldRefreshReward?: boolean;
}) => {
  const {
    spuIds, categoryId, algorithmPriceFlag, isFromDetail,
    shouldRefreshReward = false,
  } = options;

  // 1. 如需刷新奖励金，先请求奖励金接口（顺序要求：奖励金先于列表/详情）
  if (shouldRefreshReward) {
    // 标记刷新中，防止 setDiscountData 触发 mallList 的 watch 导致列表重载、浮层销毁
    store.commit('reward/setPostOrderRefreshing', true);
    try {
      const discountData = await fetchUserDiscount();
      // 接口返回无 data 时视为"没有奖励金"，主动清零
      store.commit('reward/setDiscountData', discountData || { points: 0, amount: 0, pointSwitch: false });
      store.commit('reward/updateBarVisibility');
    } finally {
      // 延迟重置：Vue 2 watcher 在微任务中批量 flush，
      // 如果同步重置，watch 回调执行时标记已为 false，检查失效。
      // nextTick 确保 watch 回调先执行（看到标记为 true 并跳过），再重置标记。
      nextTick(() => {
        store.commit('reward/setPostOrderRefreshing', false);
      });
    }
  }

  // 2. 刷新商品列表优惠数据（使用 mall.goods.promo）
  // cityCode 必填，需先获取城市信息
  const cityInfo = await nativeMgr.getCityInfo();
  const cityCode = cityInfo?.cityCode || '';

  if (spuIds.length > 0) {
    // 过滤无效的 spuId（如两轮卡商品没有 spuId，会产生 "undefined" 字符串）
    const validSpuIds = spuIds.filter((id) => id && id !== 'undefined');
    if (validSpuIds.length === 0) return;
    fetchGoodsPromo({
      spuIds: validSpuIds.slice(0, 200), // 接口限制最多 200 个
      userNewId: window.$$user?.userNewId,
      categoryId,
      algorithmPriceFlag,
      applicationScene: 'couponMall',
      cityCode,
      _uuid: window.$$user?.userGuid,
      systemCode: getSystemCode(),
    }).then((promoData) => {
      if (promoData) {
        // 直接传入接口返回数据，由 mutation 内部按 spuId 匹配全量更新
        store.commit('ticket/updateSkuPromoInfo', promoData);
      }
    }).catch(() => { /* 列表刷新失败不阻塞 */ });
  }

  // 3. 详情页场景：通知详情页组件重新请求数据
  if (isFromDetail) {
    store.commit('reward/triggerDetailRefresh');
  }
};

/** SkuPromoInfo 类型引用（避免循环依赖，仅用于条件判断） */
interface PromoInfoLike {
  couponPromoType: number | string;
  [key: string]: unknown;
}

/**
 * 下单后统一刷新入口（需求变更V2）
 *
 * 封装刷新条件判断 + refreshRewardData 调用，order.ts 和 pay.ts 共用。
 * 刷新失败不阻塞购买流程。
 */
export const handlePostOrderRefresh = (options: {
  /** 下单商品的 promotionInfos */
  promotionInfos?: PromoInfoLike[];
  /** 下单商品的 optionalPromotions */
  optionalPromotions?: PromoInfoLike[];
  /** 是否来自详情页/浮层入口（由调用方显式传入） */
  isFromDetail: boolean;
  /** 当前所在分类 */
  categoryId: string;
  /** 算法出价开关 */
  algorithmPriceFlag?: string;
  /** 当前展示的所有商品 spuId 列表 */
  allSpuIds: string[];
}) => {
  const {
    promotionInfos, optionalPromotions, isFromDetail,
    categoryId, algorithmPriceFlag, allSpuIds,
  } = options;

  const rewardState = store.state.reward;
  const hasPromotionInfos = !!(promotionInfos?.length);
  const hasOptionalPromotions = !!(optionalPromotions?.length);
  const isRewardChecked = !!rewardState?.rewardChecked;

  let needRefresh = false;
  let shouldRefreshReward = false;

  if (isFromDetail) {
    // 详情页/浮层入口
    if (hasPromotionInfos) {
      needRefresh = true;
      shouldRefreshReward = isRewardChecked;
    } else if (hasOptionalPromotions && isRewardChecked) {
      needRefresh = true;
      shouldRefreshReward = true;
    }
  } else if (hasPromotionInfos) {
    // 列表页入口：promotionInfos 有数据 → 刷新列表；有奖励金优惠 → 额外刷新奖励金
    needRefresh = true;
    shouldRefreshReward = !!(promotionInfos?.some(
      (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
    ));
  }

  if (!needRefresh) return;

  refreshRewardData({
    spuIds: allSpuIds,
    categoryId,
    algorithmPriceFlag,
    isFromDetail,
    shouldRefreshReward,
  }).catch(() => { /* 刷新失败不阻塞 */ });
};
